import algoliasearch, { SearchIndex } from 'algoliasearch';
import { SearchResponse } from '@algolia/client-search';
import { makeServiceTerms } from '@/utils/algolia';
import { SearchLocation } from '@/utils/search';
import { CLIENT_ENV } from '@/config/configuration';
import { AlgoliaClinicV2 } from '@/types/algolia-types';
import { ChannelFilterType, DateFilterType, FilterProps, SortingListingType } from '@/api/algolia/types';

export const generateAlgoliaClient = (key: string) => {
  const appId = CLIENT_ENV().NEXT_PUBLIC_ALGOLIA_APP_ID;
  const CLINIC_IDX = CLIENT_ENV().NEXT_PUBLIC_ALGOLIA_INDEX;

  const algoliaClient = algoliasearch(appId, key);
  const clinicIndex = algoliaClient.initIndex(CLINIC_IDX);
  const servicePriceAscIndex = algoliaClient.initIndex(`${CLINIC_IDX}_service_price_asc`);
  const firstAvailabilityAscIndex = algoliaClient.initIndex(`${CLINIC_IDX}_first_availability_asc`);
  const noDistinctIndex = algoliaClient.initIndex(`${CLINIC_IDX}_no-distinct`);
  const nomenclatoreIndex = algoliaClient.initIndex('nomenclatore');

  return {
    clinicIndex,
    servicePriceAscIndex,
    firstAvailabilityAscIndex,
    noDistinctIndex,
    nomenclatoreIndex,
  };
};

const publicAlgoliaClient = generateAlgoliaClient(CLIENT_ENV().NEXT_PUBLIC_ALGOLIA_SEARCH_KEY);

const dateFilterMs = {
  [DateFilterType.three_days]: 3,
  [DateFilterType.seven_days]: 7,
};

const getTimestampInDays = (daysFromNow: number): number => {
  const now = new Date();
  const targetDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysFromNow, 0, 0, 0, 0));
  return targetDate.getTime();
};

const sortingIndexes = {
  [SortingListingType.distanceAsc]: publicAlgoliaClient.clinicIndex,
  [SortingListingType.priceAsc]: publicAlgoliaClient.servicePriceAscIndex,
  [SortingListingType.firstAvailabilityAsc]: publicAlgoliaClient.firstAvailabilityAscIndex,
  [SortingListingType.noDistinct]: publicAlgoliaClient.noDistinctIndex,
};

type AlgoliaSearchRequestParams = {
  text: string;
  page?: number;
  filterObj?: null | FilterProps;
  hitsPerPage?: number;
  searchLocation?: null | SearchLocation;
  order?: SortingListingType;
  defaultIndex?: SearchIndex;
};

export type AlgoliaSuggestionsRequestParams = {
  text: string;
  filterObj?: null | FilterProps;
};

const getFacetFilters = (filterObj: FilterProps | null) => {
  return [
    ...(filterObj?.specialization ? [`specializations.macroSpeciality:${filterObj?.specialization}`] : []),
    ...(filterObj?.doctorSex ? [`specializations.doctor.sex:${filterObj?.doctorSex}`] : []),
    ...(filterObj?.license ? [`license:${filterObj?.license}`] : []),
    // char '-' negate the facet filter https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/#usage-notes
    ...(filterObj?.excludeDomicilary ? [`specializations.channels:-DOMICILIARY`] : []),
    ...(filterObj?.excludeOnline ? [`specializations.channels:-VIDEO`] : []),
    ...(filterObj?.excludeOnsite ? [`specializations.channels:-STUDIO`] : []),
    ...(filterObj?.paymentType ? [`availablePayments:${filterObj.paymentType}`] : []),
    ...(filterObj?.channel
      ? [
          filterObj.channel.map(selectedChannel => {
            if (selectedChannel === ChannelFilterType.domiciliary) {
              return `domiciliaryCare:true`;
            }
            return `specializations.specialityChannels:${selectedChannel}`;
          }),
        ]
      : []),
    ...(filterObj?.ambulatoryId ? [`ambulatoryId:${filterObj.ambulatoryId}`] : []),
  ];
};

// Used in search page
export const getAlgoliaSearchResponse = async ({
  text,
  searchLocation = null,
  order,
  page = 0,
  filterObj = null,
  hitsPerPage = 20,
  defaultIndex = publicAlgoliaClient.clinicIndex,
}: AlgoliaSearchRequestParams): Promise<SearchResponse<AlgoliaClinicV2>> => {
  const currentIndex = order ? sortingIndexes[order] : defaultIndex;
  return await currentIndex?.search<AlgoliaClinicV2>(text, {
    page,
    numericFilters: [
      ...(filterObj?.dateFilter ? [`firstAvailableVisitTimestamp < ${getTimestampInDays(dateFilterMs[filterObj.dateFilter])}`] : []),
      ...(filterObj?.excludePrice0 ? [`specializations.price > 0`] : []),
    ],
    facets: ['specializations.price', 'firstAvailableVisitTimestamp'],
    facetFilters: getFacetFilters(filterObj),
    hitsPerPage,
    getRankingInfo: true,
    aroundRadius: !!searchLocation?.latitude && !!searchLocation?.longitude ? 30000 : 'all',
    ...(!!searchLocation?.latitude && !!searchLocation?.longitude
      ? { aroundLatLng: `${searchLocation.latitude}, ${searchLocation.longitude}` }
      : { aroundLatLngViaIP: true }),
    // alternativesAsExact: ['singleWordSynonym'],
  });
};

// Used in autocomplete
export const getAlgoliaSearchSuggestions = async ({ text }: AlgoliaSuggestionsRequestParams) => {
  const clinicAutocompleteIndex = publicAlgoliaClient.nomenclatoreIndex;
  const searchResponse = await clinicAutocompleteIndex.search<AlgoliaClinicV2>(text, {});

  console.log('searchResponse', searchResponse);
  const serviceTerms = makeServiceTerms(searchResponse);

  return [...serviceTerms];
};

export type ClinicSearchMatcherResult = {
  matchLevel?: string;
  matchedWords: string[];
  value?: string;
};

export type ClinicSearchResult = SearchResponse<AlgoliaClinicV2>;
